<script setup lang="ts">
  import { Icon, IconName, IconSize, Input, Typo, TypoVariant } from '@travelata-libs/ui-kit';
  import { computed, onBeforeUnmount, ref, watch } from 'vue';

  import type { Country, Destination, DestinationPrices } from '../model/types';
  import { isCountry, type Selection } from '../model/selection';
  import { useDestination } from '../model/useDestination';
  import DestinationDropdown from './DestinationDropdown.vue';
  import { DESTINATION_TEXTS } from '../config/texts';

  interface DestinationFieldProps {
    modelValue?: Selection;
    emptyByDefault?: boolean;
    countries?: Country[];
    disabled?: boolean; 
    prices?: DestinationPrices;
  }

  const props = withDefaults(defineProps<DestinationFieldProps>(), {
    modelValue: undefined,
    emptyByDefault: false,
    countries: () => [],
    disabled: false,
    prices: undefined,
  });

  interface DestinationFieldEmits {
    (e: 'update:modelValue', selection: Selection): void;
    (e: 'change', selection: Selection): void;
  }

  const emit = defineEmits<DestinationFieldEmits>();

  const dest = useDestination({
    initialCountries: props.countries ?? [],
    prices: () => props.prices,
  });

  watch(
    () => props.countries,
    (list) => dest.setCountries(list ?? []),
  );

  watch(
    () => props.prices,
    () => {
      dest.setCountries(props.countries ?? []);
      dest.repriceResorts();
    },
    { deep: true },
  );

  const showSelectAll = computed<boolean>(() => dest.selectAllVisible.value);

  const isCountrySelected = computed<boolean>(() => isCountry(dest.selection.value));

  const inputRef = ref<{ focus: () => void } | null>(null);

  const focusInput = (): void => {
    inputRef.value?.focus();
  };

  const onOpen = (): void => {
    dest.open();
  };

  const onSelect = (option: Destination): void => {
    dest.selectDestination(option);
  };

  const onKeydown = (event: KeyboardEvent): void => {
    if (event.key === 'Escape') {
      dest.close();
    }
  };

  const onClear = (): void => {
    dest.clearInput();
  };

  const rootRef = ref<HTMLElement | null>(null);

  const onOutsidePointer = (event: MouseEvent): void => {
    const root = rootRef.value;
    const target = event.target as Node | null;
    if (root && target && !root.contains(target)) {
      dest.close();
    }
  };

  watch(dest.controlActivated, (active) => {
    if (active) {
      document.addEventListener('mousedown', onOutsidePointer);
    } else {
      document.removeEventListener('mousedown', onOutsidePointer);
    }
  });

  onBeforeUnmount(() => {
    document.removeEventListener('mousedown', onOutsidePointer);
  });
  let lastEmitted: Selection | undefined;

  watch(dest.selection, (selection) => {
    lastEmitted = selection;
    emit('update:modelValue', selection);
    emit('change', selection);
  });

  watch(() => props.modelValue, (value) => {
    if (value && value !== lastEmitted && !dest.controlActivated.value) {
      dest.restore(value);
    }
  }, { immediate: true })

  defineExpose({
    validate: (): boolean => dest.validate(),
    clear: (): void => dest.clear(),
  });
</script>

<template>
  <div ref="rootRef" class="destination-field" data-testid="destination-field">
    <DestinationDropdown
      :options="dest.optionsList.value"
      :selection="dest.selection.value"
      :loading="dest.loading.value"
      :visible="dest.controlActivated.value"
      :show-select-all="showSelectAll"
      :select-all-checked="isCountrySelected"
      :select-all-country="dest.selectedCountry.value"
      @select="onSelect"
      @select-all="dest.selectAllResorts"
      @close="dest.close"
    >
      <template #reference>
        <div
          class="destination-field__control"
          :class="{
            'destination-field__control_active': dest.controlActivated.value,
            'destination-field__control_disabled': disabled,
          }"
          @click="focusInput"
        >
          <Typo
            class="destination-field__label"
            :variant="TypoVariant.BodySRegular"
            tag="span"
          >{{ DESTINATION_TEXTS.fieldLabelFixed }}</Typo>

          <div class="destination-field__value">
            <Input
              ref="inputRef"
              :model-value="dest.inputString.value"
              :placeholder="DESTINATION_TEXTS.placeholder"
              :error="dest.invalid.value"
              :bg="false"
              :disabled="disabled"
              :bordered="false"
              name="input"
              size="xs"
              data-testid="destination-input"
              @update:modelValue="dest.setInput"
              @focus="onOpen"
              @click="onOpen"
              @keydown.native="onKeydown"
              class="destination-field__input"
            >
              <template #append>
                <button
                  v-if="dest.clearVisible.value"
                  type="button"
                  class="destination-field__clear"
                  data-testid="destination-clear"
                  aria-label="Очистить"
                  @click.stop.prevent="onClear"
                >
                  <Icon :name="IconName.Close" :size="IconSize.S" />
                </button>
              </template>
            </Input>
          </div>
        </div>
      </template>
    </DestinationDropdown>

    <div
      v-if="dest.errorText.value"
      class="destination-field__error"
      data-testid="destination-error"
    >
      <Typo :variant="TypoVariant.BodySRegular" tag="span">{{ dest.errorText.value }}</Typo>
    </div>
  </div>
</template>

<style scoped lang="scss">
  :deep(.destination-field__input input[name="input"]) {
    font-size: 16px;
  }
  .destination-field {
    position: relative;
    width: 100%;
    $root: &;

    &__control {
      background: var(--color-bg-primary);
      cursor: text;
      display: flex;
      flex-direction: column;
      gap: var(--spacing-xxxs);
      padding: var(--spacing-xs) var(--spacing-s);
      transition: background-color var(--transition-default);

      &_active {
        background: var(--color-disabled-100);
      }
      &_disabled {
        background: var(--color-disabled-100);
        #{$root}__label {
          color: var(--color-disabled-900);
        }
      }
    }

    &__label {
      color: var(--color-text-secondary);
      pointer-events: none;
      user-select: none;
    }

    &__value {
      :deep(.tl-input) {
        padding: 0;
      }
    }

    &__clear {
      align-items: center;
      background: none;
      border: none;
      cursor: pointer;
      display: inline-flex;
      justify-content: center;
      padding: 0;
      color: var(--color-text-secondary);
    }

    &__error {
      color: var(--color-error-500);
      margin-top: var(--spacing-2xs);
    }
  }
</style>
